import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Alert,
  AlertIcon,
  Avatar,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Text,
  useClipboard,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { Select } from "chakra-react-select";
import type { InstagramPartnerPermissionStatusWire } from "@common/interfaces/adsManagerGuidedEdit";
import { isApprovedBrandedContentPermissionStatus } from "@common/utils/metaPartnershipAdCreative";
import {
  creativeBlockHasPartnerPost,
  instagramPartnerPermissionLaunchMessage,
} from "@common/utils/instagramPartnershipCreative";
import {
  buildAdvertiserPartnershipValidationMessages,
  hasRequestablePartnerInstagramForPermissionRequest,
  isPartnershipPartnerOptionDisabled,
  isPartnershipPermissionRequestOutstanding,
  resolvePartnershipPartnerApprovalLinks,
  resolvePartnershipPartnerRequestIdentity,
  resolvePartnershipSelectBadge,
  shouldShowPartnershipPartnerApprovalLinks,
  shouldShowRequestPartnershipButton,
  type PartnershipPartnerApprovalLink,
  type PartnershipSelectBadgeKind,
} from "@common/utils/partnershipPartnerOptions";
import actions from "src/actions";
import type { CreativeBlock } from "../guidedFlow/types";
import type { FacebookPage, InstagramAccount } from "../types";
import {
  filterSelectOptionsExcludingId,
  isSameMetaAssetId,
  normalizeMetaAssetId,
} from "../utils/partnerIdentitySelectOptions";
import {
  enrichFacebookPageWithDeclaredLinkedInstagram,
  facebookPageWithCatalogLinkedMetadata,
  facebookPageWithResolvedLinkedInstagram,
} from "../utils/creativesWithInstagramFromFetch";
import { mergeInstagramAccounts } from "../utils/mergeInstagramAccounts";
import {
  facebookPageHasLinkedInstagram,
  isPartnerFacebookPageCompatibleWithInstagram,
  isPartnerFacebookPageInferredFromInstagramSelection,
  partnerFacebookFieldsAfterInstagramSelected,
  partnerFacebookFieldsFromLinkedInstagramAccount,
  partnerInstagramFieldsAfterFacebookPageSelected,
  partnerInstagramFieldsFromLinkedFacebookPage,
  isSyntheticPartnerInstagramUserId,
  resolveCreativePartnerFacebookPage,
  resolveCreativePartnerInstagramAccount,
  resolvePartnerIdsForGuidedWire,
  resolvePartnerInstagramAccountByWire,
} from "../utils/resolveCreativePartnerDisplay";
import {
  mergePartnershipPermissionLookupIntoFacebookPage,
  mergePartnershipPermissionLookupIntoInstagramAccount,
  resolvePartnershipPermissionLookupWire,
} from "../utils/partnershipPartnerPermissionLookup";
import { resolveAdvertiserIdentityForPartnership } from "../utils/partnershipPartnerOptionsFetch";
import {
  CREATIVE_PARTNER_SELECT_CONTROL_Z,
  CREATIVE_PARTNER_SELECT_MENU_Z,
  withCreativeSelectStackOrder,
} from "../utils/creativeBlockSelectStyles";
import PartnershipStatusBadge from "./PartnershipStatusBadge";
import RequestPartnershipPermissionModal from "./RequestPartnershipPermissionModal";

type PartnerSelection = {
  label: string;
  partner: FacebookPage | InstagramAccount;
  partnerInstagramUserId?: string;
  partnerInstagramUsername?: string;
  partnerFacebookPageId?: string;
};

function resolvePanelPartnerRequestIdentity(args: {
  page: FacebookPage | null | undefined;
  instagram: InstagramAccount | null | undefined;
  creative: CreativeBlock;
}) {
  return resolvePartnershipPartnerRequestIdentity({
    partnerInstagramUserId:
      args.instagram?.id ?? args.creative.partnerInstagramUserId,
    partnerInstagramUsername:
      args.instagram?.username ?? args.creative.partnerInstagramUsername,
    partnerFacebookPageId: args.page?.id ?? args.creative.partnerFacebookPageId,
    linkedInstagramUserId: args.page?.linked_instagram_user_id,
    linkedInstagramUsername: args.page?.linked_instagram_username,
  });
}

function buildPartnerSelection(args: {
  page: FacebookPage | null | undefined;
  instagram: InstagramAccount | null | undefined;
  creative: CreativeBlock;
}): PartnerSelection | null {
  const ig = args.instagram;
  const page = args.page;
  if (!ig && !page) {
    return null;
  }
  const { requestable: _requestable, ...requestIdentity } =
    resolvePanelPartnerRequestIdentity(args);
  if (page) {
    return {
      label: page.name,
      partner: page,
      ...requestIdentity,
    };
  }
  return {
    label: ig!.username ? `@${ig!.username}` : ig!.name,
    partner: ig!,
    ...requestIdentity,
  };
}

function partnerFacebookPageIdentityKey(pageId?: string | null): string {
  return pageId?.trim() || "__no_page__";
}

function partnerInstagramIdentityKey(args: {
  partnerInstagramUserId?: string | null;
  partnerInstagramUsername?: string | null;
  account?: InstagramAccount | null;
}): string | null {
  return (
    args.partnerInstagramUserId?.trim() ||
    args.partnerInstagramUsername?.trim() ||
    args.account?.id?.trim() ||
    args.account?.username?.trim() ||
    null
  );
}

function userDismissedInferredPartnerFacebookForIdentity(
  identity: {
    partnerInstagramUserId?: string;
    partnerInstagramUsername?: string;
  },
  dismissedPartnerInstagramKey: string | null
): boolean {
  const igKey = partnerInstagramIdentityKey(identity);
  return Boolean(igKey && dismissedPartnerInstagramKey === igKey);
}

const PARTNER_PAGE_LINKED_INSTAGRAM_INFERENCE = {
  inferFromFirstFetchedWhenUndeclared: false,
} as const;

type PartnershipRequestError = {
  message: string;
  partnershipAdsHubUrl: string | null;
};

function partnershipAdsHubUrlFromErrorDetails(
  details: unknown
): string | null {
  if (!details || typeof details !== "object" || Array.isArray(details)) {
    return null;
  }
  const hubUrl = (details as { partnership_ads_hub_url?: unknown })
    .partnership_ads_hub_url;
  return typeof hubUrl === "string" && hubUrl.trim() ? hubUrl.trim() : null;
}

function partnershipRequestErrorFrom(err: unknown): PartnershipRequestError {
  if (!(err instanceof Error)) {
    return {
      message: "Could not send partnership request",
      partnershipAdsHubUrl: null,
    };
  }
  const details =
    "details" in err ? (err as { details?: unknown }).details : undefined;
  return {
    message: err.message.trim() || "Could not send partnership request",
    partnershipAdsHubUrl: partnershipAdsHubUrlFromErrorDetails(details),
  };
}

function PartnershipPartnerApprovalLinkButton({
  link,
}: {
  link: PartnershipPartnerApprovalLink;
}) {
  const { onCopy, hasCopied } = useClipboard(link.url);
  return (
    <Button size="sm" variant="outline" onClick={onCopy}>
      {hasCopied ? "Copied" : link.copyLabel}
    </Button>
  );
}

type PartnershipStatusFollowUpProps = {
  partnerLabel: string;
  statusLine: ReactNode;
  partnerApprovalLinks: PartnershipPartnerApprovalLink[];
  partnershipRequestOutstanding: boolean;
  canRequestPartnership: boolean;
  onOpenRequestModal: () => void;
  requestDisabled: boolean;
  onRefresh: () => void;
  refreshing: boolean;
};

function PartnershipStatusFollowUp({
  partnerLabel,
  statusLine,
  partnerApprovalLinks,
  partnershipRequestOutstanding,
  canRequestPartnership,
  onOpenRequestModal,
  requestDisabled,
  onRefresh,
  refreshing,
}: PartnershipStatusFollowUpProps) {
  return (
    <Alert
      status="warning"
      size="sm"
      mt={3}
      borderRadius="md"
      flexDirection="column"
      alignItems="stretch"
    >
      <HStack align="flex-start" w="full" spacing={3}>
        <AlertIcon alignSelf="flex-start" mt={0.5} />
        <Box flex={1} minW={0}>
          <Text fontSize="sm">{statusLine}</Text>
          {partnerApprovalLinks.length > 0 ? (
            <Box mt={3}>
              <Text fontSize="xs" color="gray.600">
                Send {partnerLabel} an approval link so they can accept in Meta.
              </Text>
              <HStack spacing={2} flexWrap="wrap" mt={2}>
                {partnerApprovalLinks.map((link) => (
                  <PartnershipPartnerApprovalLinkButton
                    key={`${link.channel}-${link.url}`}
                    link={link}
                  />
                ))}
              </HStack>
            </Box>
          ) : null}
          <HStack spacing={2} flexWrap="wrap" mt={3}>
            <Button
              size="sm"
              variant="outline"
              onClick={onRefresh}
              isLoading={refreshing}
            >
              Refresh status
            </Button>
            {canRequestPartnership ? (
              <Button
                size="sm"
                colorScheme="gray"
                onClick={onOpenRequestModal}
                isDisabled={requestDisabled}
              >
                Request partnership
              </Button>
            ) : partnershipRequestOutstanding ? (
              <Button size="sm" variant="outline" isDisabled>
                Request sent
              </Button>
            ) : null}
          </HStack>
        </Box>
      </HStack>
    </Alert>
  );
}

type PartnerInstagramOption = {
  label: string;
  value: InstagramAccount;
  isDisabled?: boolean;
};

type PartnerFacebookOption = {
  label: string;
  value: FacebookPage;
  isDisabled?: boolean;
};

export type InstagramPartnerPanelProps = {
  creative: CreativeBlock;
  pageOptions: { label: string; value: FacebookPage }[];
  instagramOptions: { label: string; value: InstagramAccount }[];
  partnerPages?: FacebookPage[];
  partnerInstagramAccounts?: InstagramAccount[];
  partnershipOptionsLoading?: boolean;
  partnershipPermissionsLoaded?: boolean;
  partnershipOptionsError?: string | null;
  adAccountId?: string;
  advertiserInstagramUserId?: string;
  onRefreshPartnershipOptions?: () => Promise<void>;
  advertiserValidationMessages?: string[];
  isDisabled?: boolean;
  isFacebookPagesLoading?: boolean;
  isInstagramAccountsLoading?: boolean;
  onPartnerFacebookPageChange?: (
    facebookPageId: string
  ) => void | Promise<InstagramAccount[] | void>;
  onUpdate: (patch: Partial<CreativeBlock>) => void;
  onPartnershipPermissionLookupLoadingChange?: (loading: boolean) => void;
  selectContainerStyles: Record<
    string,
    (p: Record<string, unknown>) => Record<string, unknown>
  >;
};

const CLEAR_PARTNER_INSTAGRAM: Partial<CreativeBlock> = {
  partnerInstagramUserId: undefined,
  partnerInstagramUsername: undefined,
  partnerInstagramAccount: null,
};

const CLEAR_PARTNER_FACEBOOK: Partial<CreativeBlock> = {
  partnerFacebookPageId: undefined,
  partnerFacebookPage: null,
};

function withPartnerPostFlag(
  creative: CreativeBlock,
  patch: Partial<CreativeBlock>
): Partial<CreativeBlock> {
  const merged = { ...creative, ...patch };
  return {
    ...patch,
    partnerPostEnabled: creativeBlockHasPartnerPost(merged),
  };
}

const accountFieldLabelProps = {
  fontSize: "13px",
  fontWeight: "medium",
  mb: 2,
} as const;

function toFacebookSelectOptions(
  pages: readonly FacebookPage[]
): PartnerFacebookOption[] {
  return pages.map((page) => ({
    label: page.name,
    value: page,
    isDisabled: isPartnershipPartnerOptionDisabled({
      is_eligible_for_branded_content: page.is_eligible_for_branded_content,
      partnership_select_badge: page.partnership_select_badge,
    }),
  }));
}

function toInstagramSelectOptions(
  accounts: readonly InstagramAccount[]
): PartnerInstagramOption[] {
  return accounts.map((account) => ({
    label: account.username ? `@${account.username}` : account.name,
    value: account,
    isDisabled: isPartnershipPartnerOptionDisabled({
      partnership_select_badge: account.partnership_select_badge,
    }),
  }));
}

export default function InstagramPartnerPanel({
  creative,
  pageOptions,
  instagramOptions,
  partnerPages,
  partnerInstagramAccounts,
  partnershipOptionsLoading = false,
  partnershipPermissionsLoaded = false,
  partnershipOptionsError = null,
  adAccountId,
  advertiserInstagramUserId,
  onRefreshPartnershipOptions,
  isDisabled,
  isFacebookPagesLoading,
  isInstagramAccountsLoading,
  onPartnerFacebookPageChange,
  onUpdate,
  onPartnershipPermissionLookupLoadingChange,
  selectContainerStyles,
}: InstagramPartnerPanelProps) {
  const partnerFacebookPageChangeSeq = useRef(0);
  /** After the user clears an IG-inferred partner page, do not auto-fill it again for the same IG. */
  const userDismissedInferredPartnerFacebookRef = useRef<string | null>(null);
  /** After the user clears partner IG for a page, do not auto-fill linked IG again for that page. */
  const userClearedPartnerInstagramRef = useRef<string | null>(null);
  const [partnerPageFetchedInstagramAccounts, setPartnerPageFetchedInstagramAccounts] =
    useState<InstagramAccount[]>([]);
  const [partnerFacebookPageFetchLoading, setPartnerFacebookPageFetchLoading] =
    useState(false);
  const [partnerPermissionLookupLoading, setPartnerPermissionLookupLoading] =
    useState(false);
  const [selectedPartnerPermissionLoaded, setSelectedPartnerPermissionLoaded] =
    useState(true);

  const creativeRef = useRef(creative);
  creativeRef.current = creative;

  const applyPartnerUpdate = useCallback(
    (patch: Partial<CreativeBlock>) => {
      const patchWithFlag = withPartnerPostFlag(creativeRef.current, patch);
      creativeRef.current = {
        ...creativeRef.current,
        ...patchWithFlag,
      };
      onUpdate(patchWithFlag);
    },
    [onUpdate]
  );

  const mainFacebookPageId = normalizeMetaAssetId(creative.fbPage?.id);
  const mainInstagramAccountId = normalizeMetaAssetId(
    creative.instagramAccount?.id
  );
  const mainInstagramUsername = creative.instagramAccount?.username?.trim();

  const hasPartnershipCatalog = Boolean(
    partnerPages !== undefined || partnerInstagramAccounts !== undefined
  );

  const partnershipPartnerInstagramCatalog = useMemo(
    () =>
      mergeInstagramAccounts([
        ...(partnerInstagramAccounts ?? []),
        ...partnerPageFetchedInstagramAccounts,
      ]),
    [partnerInstagramAccounts, partnerPageFetchedInstagramAccounts]
  );

  const allInstagramAccounts = useMemo(
    () =>
      hasPartnershipCatalog
        ? partnershipPartnerInstagramCatalog
        : mergeInstagramAccounts([
            ...(partnerInstagramAccounts ??
              instagramOptions.map((option) => option.value)),
            ...partnerPageFetchedInstagramAccounts,
          ]),
    [
      hasPartnershipCatalog,
      instagramOptions,
      partnerInstagramAccounts,
      partnerPageFetchedInstagramAccounts,
      partnershipPartnerInstagramCatalog,
    ]
  );

  const partnershipPartnerInstagramOptions = useMemo(
    () =>
      toInstagramSelectOptions(
        filterSelectOptionsExcludingId(
          allInstagramAccounts,
          mainInstagramAccountId,
          (account) => account.id
        )
      ),
    [allInstagramAccounts, mainInstagramAccountId]
  );

  const partnershipPartnerFacebookOptions = useMemo(() => {
    const pages = hasPartnershipCatalog
      ? (partnerPages ?? [])
      : (partnerPages ?? pageOptions.map((option) => option.value));
    return toFacebookSelectOptions(
      filterSelectOptionsExcludingId(
        pages,
        mainFacebookPageId,
        (page) => page.id
      )
    );
  }, [hasPartnershipCatalog, partnerPages, pageOptions, mainFacebookPageId]);

  const allFacebookPages = useMemo(
    () =>
      hasPartnershipCatalog
        ? (partnerPages ?? [])
        : (partnerPages ?? pageOptions.map((option) => option.value)),
    [hasPartnershipCatalog, partnerPages, pageOptions]
  );

  const partnershipAdvertiserValidationMessages = useMemo(() => {
    const identity = resolveAdvertiserIdentityForPartnership(
      creative,
      instagramOptions.map((option) => option.value)
    );
    return buildAdvertiserPartnershipValidationMessages({
      advertiserPageId: identity.advertiserPageId,
      advertiserInstagramUserId: identity.advertiserInstagramUserId,
    });
  }, [creative, instagramOptions]);

  const resolvedPartnerInstagramAccount = useMemo(
    () =>
      resolveCreativePartnerInstagramAccount(creative, allInstagramAccounts),
    [creative, allInstagramAccounts]
  );

  const partnerInstagramSelectOptions = useMemo(() => {
    let options = partnershipPartnerInstagramOptions;
    const lockedPage = resolveCreativePartnerFacebookPage(
      creative,
      allFacebookPages
    );
    if (lockedPage && facebookPageHasLinkedInstagram(lockedPage)) {
      const linkedFields = partnerInstagramFieldsFromLinkedFacebookPage(
        lockedPage,
        allInstagramAccounts
      );
      const linkedAccount = linkedFields?.partnerInstagramAccount;
      if (linkedAccount) {
        options = [
          {
            label: linkedAccount.username
              ? `@${linkedAccount.username}`
              : linkedAccount.name,
            value: linkedAccount,
          },
        ];
      }
    }

    const account = resolvedPartnerInstagramAccount;
    if (!account) {
      return options;
    }
    if (options.some((option) => option.value.id === account.id)) {
      return options;
    }
    return [
      {
        label: account.username ? `@${account.username}` : account.name,
        value: account,
      },
      ...options,
    ];
  }, [
    partnershipPartnerInstagramOptions,
    resolvedPartnerInstagramAccount,
    creative,
    allFacebookPages,
    allInstagramAccounts,
  ]);

  const selectedInstagramOption = useMemo((): PartnerInstagramOption | null => {
    const account = resolvedPartnerInstagramAccount;
    if (!account) {
      return null;
    }
    return (
      partnerInstagramSelectOptions.find(
        (option) => option.value.id === account.id
      ) ?? {
        label: account.username ? `@${account.username}` : account.name,
        value: account,
      }
    );
  }, [resolvedPartnerInstagramAccount, partnerInstagramSelectOptions]);

  const selectedFacebookOption = useMemo((): PartnerFacebookOption | null => {
    const page = resolveCreativePartnerFacebookPage(creative, allFacebookPages);
    if (!page) {
      return null;
    }
    return (
      partnershipPartnerFacebookOptions.find(
        (option) => option.value.id === page.id
      ) ?? {
        label: page.name,
        value: page,
      }
    );
  }, [creative, allFacebookPages, partnershipPartnerFacebookOptions]);

  useEffect(() => {
    const partnerId = normalizeMetaAssetId(creative.partnerInstagramUserId);
    const partnerUsername = creative.partnerInstagramUsername?.trim();
    const conflictsWithMainId =
      Boolean(partnerId && mainInstagramAccountId) &&
      partnerId === mainInstagramAccountId;
    const conflictsWithMainUsername =
      Boolean(partnerUsername && mainInstagramUsername) &&
      partnerUsername === mainInstagramUsername;

    if (conflictsWithMainId || conflictsWithMainUsername) {
      const partnerPage = resolveCreativePartnerFacebookPage(
        creative,
        allFacebookPages
      );
      if (
        partnerPage &&
        facebookPageHasLinkedInstagram(partnerPage) &&
        isPartnerFacebookPageCompatibleWithInstagram(
          partnerPage,
          partnerId,
          partnerUsername,
          allInstagramAccounts
        )
      ) {
        return;
      }
      applyPartnerUpdate(CLEAR_PARTNER_INSTAGRAM);
    }
  }, [
    allFacebookPages,
    allInstagramAccounts,
    applyPartnerUpdate,
    creative,
    creative.partnerInstagramUserId,
    creative.partnerInstagramUsername,
    mainInstagramAccountId,
    mainInstagramUsername,
  ]);

  useEffect(() => {
    const partnerPageId = normalizeMetaAssetId(creative.partnerFacebookPageId);
    if (
      partnerPageId &&
      mainFacebookPageId &&
      partnerPageId === mainFacebookPageId
    ) {
      applyPartnerUpdate({
        partnerFacebookPageId: undefined,
        partnerFacebookPage: null,
        partnerPermissionStatus: undefined,
      });
    }
  }, [applyPartnerUpdate, creative.partnerFacebookPageId, mainFacebookPageId]);

  const selectedPartnerFacebookPage = selectedFacebookOption?.value;
  const partnerFacebookPageSelected = Boolean(selectedPartnerFacebookPage?.id);
  const partnerInstagramLockedToFacebookPage = Boolean(
    selectedPartnerFacebookPage &&
      facebookPageHasLinkedInstagram(selectedPartnerFacebookPage)
  );
  const partnerFacebookPageInferredFromInstagram =
    isPartnerFacebookPageInferredFromInstagramSelection({
      partnerInstagramUserId: creative.partnerInstagramUserId,
      partnerInstagramUsername: creative.partnerInstagramUsername,
      partnerFacebookPageId: creative.partnerFacebookPageId,
      pages: allFacebookPages,
      instagramAccounts: allInstagramAccounts,
    });
  /** Lock Instagram only when Meta links one to the selected Facebook page. */
  const partnerInstagramSelectDisabled =
    partnerInstagramLockedToFacebookPage &&
    !partnerFacebookPageInferredFromInstagram;
  const partnerFacebookSelectDisabled = partnerInstagramLockedToFacebookPage;
  const partnerInstagramSelected = Boolean(
    creative.partnerInstagramUserId?.trim() ||
      creative.partnerInstagramUsername?.trim()
  );
  const canClearPartnerFacebookKeepingInstagram =
    partnerInstagramSelected && !partnerInstagramLockedToFacebookPage;
  const partnerInstagramOnlyMode =
    partnerInstagramSelected && !partnerFacebookPageSelected;

  const clearPartnerInstagram = useCallback(() => {
    const snapshot = creativeRef.current;
    userClearedPartnerInstagramRef.current = partnerFacebookPageIdentityKey(
      snapshot.partnerFacebookPageId
    );
    const igDismissKey = partnerInstagramIdentityKey({
      partnerInstagramUserId: snapshot.partnerInstagramUserId,
      partnerInstagramUsername: snapshot.partnerInstagramUsername,
      account: snapshot.partnerInstagramAccount,
    });
    if (igDismissKey) {
      userDismissedInferredPartnerFacebookRef.current = igDismissKey;
    }
    const pagePermission =
      selectedPartnerFacebookPage?.partnership_permission_status as
        | InstagramPartnerPermissionStatusWire
        | undefined;
    const pageWithoutLinkedInstagram =
      snapshot.partnerFacebookPage &&
      !partnerFacebookPageInferredFromInstagram
        ? {
            ...snapshot.partnerFacebookPage,
            linked_instagram_user_id: undefined,
            linked_instagram_username: undefined,
          }
        : undefined;
    applyPartnerUpdate({
      ...CLEAR_PARTNER_INSTAGRAM,
      ...(partnerFacebookPageInferredFromInstagram
        ? CLEAR_PARTNER_FACEBOOK
        : pageWithoutLinkedInstagram
          ? { partnerFacebookPage: pageWithoutLinkedInstagram }
          : {}),
      partnerPermissionStatus: partnerFacebookPageSelected
        ? pagePermission
        : undefined,
    });
  }, [
    applyPartnerUpdate,
    partnerFacebookPageInferredFromInstagram,
    partnerFacebookPageSelected,
    selectedPartnerFacebookPage,
  ]);

  const clearPartnerFacebookSelection = useCallback(() => {
    userDismissedInferredPartnerFacebookRef.current = null;
    userClearedPartnerInstagramRef.current = null;
    setPartnerPageFetchedInstagramAccounts([]);
    applyPartnerUpdate({
      ...CLEAR_PARTNER_FACEBOOK,
      ...CLEAR_PARTNER_INSTAGRAM,
      partnerPermissionStatus: undefined,
    });
  }, [applyPartnerUpdate]);

  const clearPartnerFacebookOnly = useCallback(() => {
    userDismissedInferredPartnerFacebookRef.current =
      creative.partnerInstagramUserId?.trim() ||
      creative.partnerInstagramUsername?.trim() ||
      null;
    setPartnerPageFetchedInstagramAccounts([]);
    const permissionStatus =
      (resolvedPartnerInstagramAccount?.partnership_permission_status as
        | InstagramPartnerPermissionStatusWire
        | undefined) ?? creative.partnerPermissionStatus;
    applyPartnerUpdate({
      ...CLEAR_PARTNER_FACEBOOK,
      partnerPermissionStatus: permissionStatus,
    });
  }, [
    applyPartnerUpdate,
    creative.partnerInstagramUserId,
    creative.partnerInstagramUsername,
    creative.partnerPermissionStatus,
    resolvedPartnerInstagramAccount,
  ]);

  useEffect(() => {
    if (partnerPermissionLookupLoading) {
      return;
    }
    const page = resolveCreativePartnerFacebookPage(creative, allFacebookPages);
    if (!page) {
      return;
    }
    const lockedPartnerInstagramToFacebookPage =
      partnerFacebookPageSelected &&
      partnerInstagramLockedToFacebookPage &&
      !partnerFacebookPageInferredFromInstagram;
    if (!lockedPartnerInstagramToFacebookPage) {
      return;
    }
    if (
      userClearedPartnerInstagramRef.current ===
      partnerFacebookPageIdentityKey(creative.partnerFacebookPageId)
    ) {
      return;
    }
    const linkedFields = partnerInstagramFieldsAfterFacebookPageSelected(
      page,
      allInstagramAccounts,
      partnerPageFetchedInstagramAccounts.length > 0
        ? partnerPageFetchedInstagramAccounts
        : undefined,
      PARTNER_PAGE_LINKED_INSTAGRAM_INFERENCE
    );
    if (!linkedFields) {
      return;
    }
    const currentIds = resolvePartnerIdsForGuidedWire(creative);
    const linkedId = linkedFields.partnerInstagramUserId?.trim() ?? "";
    const linkedUsername = linkedFields.partnerInstagramUsername?.trim() ?? "";
    const accountId = linkedFields.partnerInstagramAccount?.id?.trim() ?? "";
    const needsIdSync = (currentIds.partnerInstagramUserId ?? "") !== linkedId;
    const needsUsernameSync =
      (currentIds.partnerInstagramUsername ?? "") !== linkedUsername;
    const needsAccountSync =
      Boolean(linkedFields.partnerInstagramAccount) &&
      (creative.partnerInstagramAccount?.id ?? "") !== accountId;
    if (!needsIdSync && !needsUsernameSync && !needsAccountSync) {
      return;
    }
    const permissionStatus =
      (linkedFields.partnerInstagramAccount?.partnership_permission_status as
        | InstagramPartnerPermissionStatusWire
        | undefined) ??
      (page.partnership_permission_status as
        | InstagramPartnerPermissionStatusWire
        | undefined);
    applyPartnerUpdate({
      ...linkedFields,
      partnerPermissionStatus: permissionStatus,
    });
  }, [
    allFacebookPages,
    allInstagramAccounts,
    applyPartnerUpdate,
    partnerFacebookPageInferredFromInstagram,
    partnerFacebookPageSelected,
    partnerInstagramLockedToFacebookPage,
    partnerPermissionLookupLoading,
    creative.partnerFacebookPageId,
    creative.partnerInstagramAccount?.id,
    partnerPageFetchedInstagramAccounts,
  ]);

  const resolvedPartnerInstagramAccountId =
    resolvedPartnerInstagramAccount?.id?.trim() ?? "";
  const resolvedPartnerInstagramAccountUsername =
    resolvedPartnerInstagramAccount?.username?.trim() ?? "";

  useEffect(() => {
    if (partnerPermissionLookupLoading) {
      return;
    }
    if (
      !resolvedPartnerInstagramAccountId &&
      !resolvedPartnerInstagramAccountUsername
    ) {
      return;
    }
    if (creative.partnerFacebookPageId?.trim()) {
      return;
    }
    const partnerIgKey =
      resolvedPartnerInstagramAccountId ||
      resolvedPartnerInstagramAccountUsername;
    if (
      partnerIgKey &&
      userDismissedInferredPartnerFacebookRef.current === partnerIgKey
    ) {
      return;
    }
    const linkedPageFields = partnerFacebookFieldsFromLinkedInstagramAccount(
      resolvedPartnerInstagramAccount!,
      allFacebookPages
    );
    if (!linkedPageFields) {
      return;
    }
    const nextPageId = linkedPageFields.partnerFacebookPageId?.trim() ?? "";
    if (
      !nextPageId ||
      isSameMetaAssetId(nextPageId, mainFacebookPageId)
    ) {
      return;
    }
    applyPartnerUpdate(linkedPageFields);
  }, [
    allFacebookPages,
    applyPartnerUpdate,
    creative.partnerFacebookPageId,
    mainFacebookPageId,
    partnerPermissionLookupLoading,
    resolvedPartnerInstagramAccountId,
    resolvedPartnerInstagramAccountUsername,
  ]);

  const hasSelectedPartnerIdentity = Boolean(
    creative.partnerFacebookPageId?.trim() ||
      creative.partnerInstagramUserId?.trim() ||
      creative.partnerInstagramUsername?.trim()
  );
  const partnershipPermissionLookupActive =
    hasSelectedPartnerIdentity && partnerPermissionLookupLoading;

  const facebookLoading =
    isFacebookPagesLoading ||
    (hasPartnershipCatalog && partnershipOptionsLoading) ||
    partnershipPermissionLookupActive;
  const instagramLoading =
    isInstagramAccountsLoading ||
    partnerFacebookPageFetchLoading ||
    (hasPartnershipCatalog && partnershipOptionsLoading) ||
    partnershipPermissionLookupActive;

  const partnershipPartnerFieldsLoading = facebookLoading || instagramLoading;

  const partnerInstagramPlaceholder = partnershipPermissionLookupActive
    ? "Loading partnership status…"
    : instagramLoading
      ? "Loading accounts…"
      : "Select Instagram";

  const partnerFacebookSelectStyles = withCreativeSelectStackOrder(
    selectContainerStyles,
    CREATIVE_PARTNER_SELECT_CONTROL_Z + 1,
    CREATIVE_PARTNER_SELECT_MENU_Z
  );
  const partnerInstagramSelectStyles = withCreativeSelectStackOrder(
    selectContainerStyles,
    CREATIVE_PARTNER_SELECT_CONTROL_Z,
    CREATIVE_PARTNER_SELECT_MENU_Z
  );

  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestError, setRequestError] =
    useState<PartnershipRequestError | null>(null);
  const [requestJustSent, setRequestJustSent] = useState(false);
  const [pendingPartnershipPermission, setPendingPartnershipPermission] =
    useState<{ url?: string; id?: string } | null>(null);
  const [refreshingStatus, setRefreshingStatus] = useState(false);

  useEffect(() => {
    onPartnershipPermissionLookupLoadingChange?.(
      partnerPermissionLookupLoading
    );
  }, [
    onPartnershipPermissionLookupLoadingChange,
    partnerPermissionLookupLoading,
  ]);

  useEffect(() => {
    return () => onPartnershipPermissionLookupLoadingChange?.(false);
  }, [onPartnershipPermissionLookupLoadingChange]);

  /** Partnership permission labels for the selected partner load via lazy lookup. */
  const partnershipStatusAlertsReady =
    !partnershipPartnerFieldsLoading &&
    (!hasPartnershipCatalog ||
      (!partnershipOptionsLoading &&
        !refreshingStatus &&
        !partnerPermissionLookupLoading &&
        selectedPartnerPermissionLoaded));

  const selectedPartner = useMemo(
    () =>
      buildPartnerSelection({
        page: selectedFacebookOption?.value,
        instagram: selectedInstagramOption?.value,
        creative,
      }),
    [selectedFacebookOption, selectedInstagramOption, creative]
  );

  const selectedPartnerPermissionStatus = ((
    partnerPermissionLookupLoading
      ? "UNKNOWN"
      : creative.partnerPermissionStatus &&
          creative.partnerPermissionStatus !== "UNKNOWN"
        ? creative.partnerPermissionStatus
        : undefined
  ) ??
    selectedPartnerFacebookPage?.partnership_permission_status ??
    selectedInstagramOption?.value.partnership_permission_status ??
    creative.partnerPermissionStatus ??
    "UNKNOWN") as InstagramPartnerPermissionStatusWire;

  const partnerPartnershipApproved = isApprovedBrandedContentPermissionStatus(
    selectedPartnerPermissionStatus
  );

  const selectedPartnershipPermissionUrl =
    pendingPartnershipPermission?.url?.trim() ||
    selectedPartner?.partner.partnership_permission_url?.trim() ||
    undefined;

  const selectedPartnershipPermissionId =
    pendingPartnershipPermission?.id?.trim() ||
    selectedPartner?.partner.partnership_permission_id?.trim() ||
    undefined;

  const partnershipPermissionMeta = useMemo(
    () => ({
      partnership_permission_status: selectedPartnerPermissionStatus,
      partnership_has_permission_row:
        selectedPartner?.partner.partnership_has_permission_row,
      partnership_permission_url: selectedPartnershipPermissionUrl,
      partnership_permission_id: selectedPartnershipPermissionId,
    }),
    [
      selectedPartner,
      selectedPartnerPermissionStatus,
      selectedPartnershipPermissionUrl,
      selectedPartnershipPermissionId,
    ]
  );

  const partnershipRequestOutstanding = useMemo(() => {
    if (!selectedPartner) {
      return false;
    }
    if (requestJustSent) {
      return true;
    }
    return isPartnershipPermissionRequestOutstanding(partnershipPermissionMeta);
  }, [requestJustSent, selectedPartner, partnershipPermissionMeta]);

  const selectedPartnerSelectBadge = useMemo((): {
    partnership_select_badge: PartnershipSelectBadgeKind;
    partnership_select_badge_label: string;
  } | null => {
    if (!partnerFacebookPageSelected && !partnerInstagramSelected) {
      return null;
    }
    const partner =
      selectedPartner?.partner ??
      selectedPartnerFacebookPage ??
      selectedInstagramOption?.value;

    if (
      !requestJustSent &&
      isApprovedBrandedContentPermissionStatus(selectedPartnerPermissionStatus)
    ) {
      return {
        partnership_select_badge: "approved",
        partnership_select_badge_label:
          partner?.partnership_select_badge_label ?? "Approved",
      };
    }

    return resolvePartnershipSelectBadge({
      partnership_permission_status: requestJustSent
        ? "PENDING"
        : selectedPartnerPermissionStatus,
      partnership_status_label: partner?.partnership_status_label ?? "",
      partnership_has_permission_row:
        partnershipPermissionMeta.partnership_has_permission_row ?? false,
      partnership_permission_url: selectedPartnershipPermissionUrl,
      partnership_permission_id: selectedPartnershipPermissionId,
      is_eligible_for_branded_content:
        partner && "is_eligible_for_branded_content" in partner
          ? partner.is_eligible_for_branded_content
          : undefined,
      is_ad_account_page:
        selectedPartnerFacebookPage?.is_ad_account_page ??
        selectedInstagramOption?.value.is_ad_account_page,
    });
  }, [
    selectedPartnerPermissionStatus,
    partnerFacebookPageSelected,
    partnerInstagramSelected,
    partnershipPermissionMeta.partnership_has_permission_row,
    requestJustSent,
    selectedInstagramOption?.value,
    selectedPartner,
    selectedPartnerFacebookPage,
    selectedPartnershipPermissionId,
    selectedPartnershipPermissionUrl,
  ]);

  const partnerApprovalLinks = useMemo(
    () =>
      resolvePartnershipPartnerApprovalLinks({
        partnership_permission_url: selectedPartnershipPermissionUrl,
        partnership_permission_id: selectedPartnershipPermissionId,
      }),
    [selectedPartnershipPermissionUrl, selectedPartnershipPermissionId]
  );

  const visiblePartnerApprovalLinks = useMemo(
    () =>
      shouldShowPartnershipPartnerApprovalLinks({
        requestJustSent,
        partnershipRequestOutstanding,
      })
        ? partnerApprovalLinks
        : [],
    [partnerApprovalLinks, partnershipRequestOutstanding, requestJustSent]
  );

  useEffect(() => {
    if (!partnershipStatusAlertsReady || partnerPermissionLookupLoading) {
      return;
    }
    const statusToPersist = selectedPartnerPermissionStatus;
    if (
      !statusToPersist ||
      statusToPersist === creative.partnerPermissionStatus
    ) {
      return;
    }
    applyPartnerUpdate({ partnerPermissionStatus: statusToPersist });
  }, [
    applyPartnerUpdate,
    creative.partnerPermissionStatus,
    partnerPermissionLookupLoading,
    selectedPartnerPermissionStatus,
    partnershipStatusAlertsReady,
  ]);

  const partnerLaunchBlockMessage = instagramPartnerPermissionLaunchMessage(
    selectedPartnerPermissionStatus
  );

  useEffect(() => {
    if (partnerPartnershipApproved) {
      setRequestJustSent(false);
      setPendingPartnershipPermission(null);
    }
  }, [partnerPartnershipApproved]);

  const partnerLookupIdentityKey = useMemo(() => {
    return resolvePartnershipPermissionLookupWire({
      creative,
      allFacebookPages,
      allInstagramAccounts,
      adAccountId,
      advertiserInstagramUserId,
    }).lookupKey;
  }, [
    adAccountId,
    advertiserInstagramUserId,
    allFacebookPages,
    allInstagramAccounts,
    creative.fbPage?.id,
    creative.partnerFacebookPageId,
    creative.partnerFacebookPage?.id,
    creative.partnerInstagramUserId,
    creative.partnerInstagramUsername,
    creative.partnerInstagramAccount?.id,
    creative.partnerInstagramAccount?.username,
  ]);

  const lastCompletedPartnerLookupKeyRef = useRef<string | null>(null);
  const partnerLookupInFlightKeyRef = useRef<string | null>(null);
  const partnerLookupAbortControllerRef = useRef<AbortController | null>(null);

  const abortInFlightPartnerPermissionLookup = useCallback(() => {
    partnerLookupAbortControllerRef.current?.abort();
    partnerLookupAbortControllerRef.current = null;
  }, []);

  const allFacebookPagesRef = useRef(allFacebookPages);
  allFacebookPagesRef.current = allFacebookPages;
  const allInstagramAccountsRef = useRef(allInstagramAccounts);
  allInstagramAccountsRef.current = allInstagramAccounts;

  const resolveCurrentPartnerLookupIdentityKey = useCallback(() => {
    return resolvePartnershipPermissionLookupWire({
      creative: creativeRef.current,
      allFacebookPages: allFacebookPagesRef.current,
      allInstagramAccounts: allInstagramAccountsRef.current,
      adAccountId: adAccountId?.trim(),
      advertiserInstagramUserId: advertiserInstagramUserId?.trim(),
    }).lookupKey;
  }, [adAccountId, advertiserInstagramUserId]);

  const isStalePartnerPermissionLookup = useCallback(
    (lookupKeyForRequest: string) =>
      lookupKeyForRequest !== resolveCurrentPartnerLookupIdentityKey(),
    [resolveCurrentPartnerLookupIdentityKey]
  );

  const lookupSelectedPartnerPermission = useCallback(
    async (options?: { force?: boolean }) => {
      const accountId = adAccountId?.trim();
      const brandIg = advertiserInstagramUserId?.trim();
      const creativeSnapshot = creativeRef.current;
      const { lookupKey, page, instagram, identity } =
        resolvePartnershipPermissionLookupWire({
          creative: creativeSnapshot,
          allFacebookPages: allFacebookPagesRef.current,
          allInstagramAccounts: allInstagramAccountsRef.current,
          adAccountId: accountId,
          advertiserInstagramUserId: brandIg,
        });

      if (!lookupKey) {
        lastCompletedPartnerLookupKeyRef.current = null;
        setSelectedPartnerPermissionLoaded(true);
        return;
      }

      if (
        !options?.force &&
        partnerLookupInFlightKeyRef.current === lookupKey
      ) {
        return;
      }
      if (
        !options?.force &&
        lastCompletedPartnerLookupKeyRef.current === lookupKey
      ) {
        setSelectedPartnerPermissionLoaded(true);
        return;
      }

      abortInFlightPartnerPermissionLookup();
      const abortController = new AbortController();
      partnerLookupAbortControllerRef.current = abortController;
      const lookupKeyForRequest = lookupKey;

      partnerLookupInFlightKeyRef.current = lookupKeyForRequest;
      setPartnerPermissionLookupLoading(true);
      setSelectedPartnerPermissionLoaded(false);
      try {
        const result = await actions.ADMIN.lookupPartnershipPartnerPermission({
          ad_account_id: accountId!,
          advertiser_page_id: creativeSnapshot.fbPage?.id?.trim(),
          advertiser_instagram_user_id: brandIg!,
          partner_facebook_page_id: identity.partnerFacebookPageId,
          partner_instagram_user_id: identity.partnerInstagramUserId,
          partner_instagram_username: identity.partnerInstagramUsername,
          signal: abortController.signal,
        });

        if (isStalePartnerPermissionLookup(lookupKeyForRequest)) {
          return;
        }

        const resolvedPage =
          page && identity.partnerFacebookPageId
            ? mergePartnershipPermissionLookupIntoFacebookPage(page, result)
            : undefined;
        const resolvedInstagram =
          instagram &&
          (identity.partnerInstagramUserId || identity.partnerInstagramUsername)
            ? mergePartnershipPermissionLookupIntoInstagramAccount(
                instagram,
                result
              )
            : undefined;

        const lookupPatch: Partial<CreativeBlock> = {};
        if (
          result.partnership_permission_status !==
          creativeSnapshot.partnerPermissionStatus
        ) {
          lookupPatch.partnerPermissionStatus =
            result.partnership_permission_status;
        }
        const skipInferredFacebookPagePatch =
          !creativeSnapshot.partnerFacebookPageId?.trim() &&
          userDismissedInferredPartnerFacebookForIdentity(
            identity,
            userDismissedInferredPartnerFacebookRef.current
          );
        if (
          resolvedPage &&
          identity.partnerFacebookPageId &&
          !skipInferredFacebookPagePatch &&
          !isSameMetaAssetId(
            identity.partnerFacebookPageId,
            creativeSnapshot.fbPage?.id
          )
        ) {
          const currentPage = creativeSnapshot.partnerFacebookPage;
          const pageUnchanged =
            currentPage?.id === identity.partnerFacebookPageId &&
            currentPage?.partnership_permission_status ===
              resolvedPage.partnership_permission_status &&
            currentPage?.partnership_select_badge ===
              resolvedPage.partnership_select_badge &&
            currentPage?.partnership_permission_id ===
              resolvedPage.partnership_permission_id &&
            currentPage?.partnership_permission_url ===
              resolvedPage.partnership_permission_url;
          if (!pageUnchanged) {
            lookupPatch.partnerFacebookPage = resolvedPage;
            lookupPatch.partnerFacebookPageId = identity.partnerFacebookPageId;
          }
        }
        if (resolvedInstagram) {
          const currentIg = creativeSnapshot.partnerInstagramAccount;
          const igUnchanged =
            currentIg?.id === resolvedInstagram.id &&
            currentIg?.username === resolvedInstagram.username &&
            currentIg?.partnership_permission_status ===
              resolvedInstagram.partnership_permission_status &&
            currentIg?.partnership_select_badge ===
              resolvedInstagram.partnership_select_badge;
          if (!igUnchanged) {
            lookupPatch.partnerInstagramAccount = resolvedInstagram;
            const igId = resolvedInstagram.id?.trim();
            if (igId && !isSyntheticPartnerInstagramUserId(igId)) {
              lookupPatch.partnerInstagramUserId = igId;
            }
            const igUsername = resolvedInstagram.username?.trim();
            if (igUsername) {
              lookupPatch.partnerInstagramUsername = igUsername;
            }
          }
        }

        if (Object.keys(lookupPatch).length > 0) {
          applyPartnerUpdate(lookupPatch);
        }
        if (
          isApprovedBrandedContentPermissionStatus(
            result.partnership_permission_status
          )
        ) {
          setRequestJustSent(false);
          setPendingPartnershipPermission(null);
        } else {
          setPendingPartnershipPermission(
            result.partnership_permission_url?.trim() ||
              result.partnership_permission_id?.trim()
              ? {
                  url: result.partnership_permission_url?.trim(),
                  id: result.partnership_permission_id?.trim(),
                }
              : null
          );
        }
        lastCompletedPartnerLookupKeyRef.current = lookupKeyForRequest;
      } catch (err) {
        if (
          abortController.signal.aborted ||
          isStalePartnerPermissionLookup(lookupKeyForRequest)
        ) {
          return;
        }
        applyPartnerUpdate({ partnerPermissionStatus: "UNKNOWN" });
        lastCompletedPartnerLookupKeyRef.current = lookupKeyForRequest;
      } finally {
        if (partnerLookupAbortControllerRef.current === abortController) {
          partnerLookupAbortControllerRef.current = null;
        }
        if (isStalePartnerPermissionLookup(lookupKeyForRequest)) {
          return;
        }
        if (partnerLookupInFlightKeyRef.current === lookupKeyForRequest) {
          partnerLookupInFlightKeyRef.current = null;
        }
        setPartnerPermissionLookupLoading(false);
        setSelectedPartnerPermissionLoaded(true);
      }
    },
    [
      abortInFlightPartnerPermissionLookup,
      adAccountId,
      advertiserInstagramUserId,
      applyPartnerUpdate,
      isStalePartnerPermissionLookup,
    ]
  );

  const triggerPartnerPermissionLookupOnSelection = useCallback(() => {
    lastCompletedPartnerLookupKeyRef.current = null;
    void lookupSelectedPartnerPermission({ force: true });
  }, [lookupSelectedPartnerPermission]);

  useEffect(() => {
    return () => abortInFlightPartnerPermissionLookup();
  }, [abortInFlightPartnerPermissionLookup]);

  useEffect(() => {
    if (!partnerLookupIdentityKey) {
      abortInFlightPartnerPermissionLookup();
      lastCompletedPartnerLookupKeyRef.current = null;
      setSelectedPartnerPermissionLoaded(true);
      return;
    }
    setRequestJustSent(false);
    setPendingPartnershipPermission(null);
    void lookupSelectedPartnerPermission();
    // Only re-fetch when the wire partner identity changes, not on permission metadata updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- lookupSelectedPartnerPermission is stable
  }, [partnerLookupIdentityKey]);

  const handleRefreshPartnershipStatus = useCallback(async () => {
    const page = resolveCreativePartnerFacebookPage(creative, allFacebookPages);
    const instagram = resolveCreativePartnerInstagramAccount(
      creative,
      allInstagramAccounts
    );
    const identity = resolvePanelPartnerRequestIdentity({
      page,
      instagram,
      creative,
    });
    const hasPartnerIdentity = Boolean(
      identity.partnerFacebookPageId ||
        identity.partnerInstagramUserId ||
        identity.partnerInstagramUsername
    );

    setRefreshingStatus(true);
    try {
      if (hasPartnerIdentity) {
        await lookupSelectedPartnerPermission({ force: true });
        return;
      }
      if (onRefreshPartnershipOptions) {
        await onRefreshPartnershipOptions();
      }
    } finally {
      setRefreshingStatus(false);
    }
  }, [
    allFacebookPages,
    allInstagramAccounts,
    creative,
    lookupSelectedPartnerPermission,
    onRefreshPartnershipOptions,
  ]);

  const panelPartnerRequestIdentity = useMemo(
    () =>
      resolvePanelPartnerRequestIdentity({
        page: selectedFacebookOption?.value,
        instagram: selectedInstagramOption?.value,
        creative,
      }),
    [selectedFacebookOption, selectedInstagramOption, creative]
  );

  const partnerMissingInstagramForRequest = Boolean(
    selectedPartnerFacebookPage &&
      !panelPartnerRequestIdentity.partnerInstagramUserId &&
      !panelPartnerRequestIdentity.partnerInstagramUsername &&
      !panelPartnerRequestIdentity.partnerFacebookPageId
  );

  const hasRequestablePartnerInstagram = useMemo(
    () =>
      hasRequestablePartnerInstagramForPermissionRequest({
        partnerInstagramUserId:
          panelPartnerRequestIdentity.partnerInstagramUserId,
        partnerInstagramUsername:
          panelPartnerRequestIdentity.partnerInstagramUsername,
        partnerFacebookPageId:
          panelPartnerRequestIdentity.partnerFacebookPageId,
      }),
    [panelPartnerRequestIdentity]
  );

  const canRequestPartnership = useMemo(() => {
    if (!selectedPartner || facebookLoading || instagramLoading) {
      return false;
    }
    const partner = selectedPartner.partner;
    return shouldShowRequestPartnershipButton({
      ...partnershipPermissionMeta,
      is_eligible_for_branded_content:
        "is_eligible_for_branded_content" in partner
          ? partner.is_eligible_for_branded_content
          : true,
      hasRequestablePartnerInstagram,
    });
  }, [
    selectedPartner,
    partnershipPermissionMeta,
    hasRequestablePartnerInstagram,
    facebookLoading,
    instagramLoading,
  ]);

  const partnershipStatusFollowUp = useMemo(() => {
    if (!selectedPartner || !hasPartnershipCatalog) {
      return null;
    }
    const label = selectedPartner.label;
    let statusLine: ReactNode | null = null;

    if (requestJustSent) {
      statusLine = (
        <>
          We sent a partnership request to{" "}
          <Text as="span" fontWeight="medium">
            {label}
          </Text>
          . Refresh here after they approve in Meta.
        </>
      );
    } else if (
      partnershipStatusAlertsReady &&
      partnershipRequestOutstanding
    ) {
      statusLine = (
        <>
          <Text as="span" fontWeight="medium">
            {label}
          </Text>
          {" — partnership request sent. Waiting for them to approve."}
        </>
      );
    } else if (partnershipStatusAlertsReady && canRequestPartnership) {
      statusLine = (
        <>
          <Text as="span" fontWeight="medium">
            {label}
          </Text>
          {
            " does not have an approved partnership yet. Send a partnership request first."
          }
        </>
      );
    }

    if (!statusLine) {
      return null;
    }

    return (
      <PartnershipStatusFollowUp
        partnerLabel={label}
        statusLine={statusLine}
        partnerApprovalLinks={visiblePartnerApprovalLinks}
        partnershipRequestOutstanding={partnershipRequestOutstanding}
        canRequestPartnership={canRequestPartnership}
        onOpenRequestModal={() => {
          setRequestError(null);
          setRequestModalOpen(true);
        }}
        requestDisabled={
          !advertiserInstagramUserId?.trim() || !adAccountId?.trim()
        }
        onRefresh={() => {
          void handleRefreshPartnershipStatus();
        }}
        refreshing={refreshingStatus || partnershipOptionsLoading}
      />
    );
  }, [
    adAccountId,
    advertiserInstagramUserId,
    canRequestPartnership,
    handleRefreshPartnershipStatus,
    hasPartnershipCatalog,
    visiblePartnerApprovalLinks,
    partnershipOptionsLoading,
    partnershipRequestOutstanding,
    partnershipStatusAlertsReady,
    refreshingStatus,
    requestJustSent,
    selectedPartner,
  ]);

  const handleRequestPartnership = useCallback(async () => {
    const brandIg = advertiserInstagramUserId?.trim();
    const accountId = adAccountId?.trim();
    if (!selectedPartner || !brandIg || !accountId) {
      setRequestError({
        message:
          "Select advertiser Instagram and ad account before requesting.",
        partnershipAdsHubUrl: null,
      });
      return;
    }

    setRequestLoading(true);
    setRequestError(null);
    try {
      const result = (await actions.ADMIN.requestPartnershipPartnerPermission({
        ad_account_id: accountId,
        advertiser_instagram_user_id: brandIg,
        advertiser_facebook_page_id: creative.fbPage?.id?.trim(),
        partner_instagram_user_id:
          panelPartnerRequestIdentity.partnerInstagramUserId,
        partner_instagram_username:
          panelPartnerRequestIdentity.partnerInstagramUsername,
        partner_facebook_page_id:
          panelPartnerRequestIdentity.partnerFacebookPageId,
      })) as {
        permission_id?: string;
        partnership_permission_status?: InstagramPartnerPermissionStatusWire;
        partnership_status_label?: string;
        partnership_permission_url?: string;
      };

      applyPartnerUpdate({
        partnerPermissionStatus:
          result.partnership_permission_status ?? "PENDING",
      });
      setPendingPartnershipPermission({
        url: result.partnership_permission_url?.trim(),
        id: result.permission_id?.trim(),
      });
      setRequestJustSent(true);
      setRequestModalOpen(false);
      await lookupSelectedPartnerPermission({ force: true });
    } catch (err: unknown) {
      setRequestError(partnershipRequestErrorFrom(err));
    } finally {
      setRequestLoading(false);
    }
  }, [
    adAccountId,
    advertiserInstagramUserId,
    applyPartnerUpdate,
    creative.fbPage?.id,
    panelPartnerRequestIdentity,
    lookupSelectedPartnerPermission,
    selectedPartner,
  ]);

  return (
    <Box
      w="full"
      p={3}
      bg="#FAFAFA"
      borderWidth="1px"
      borderColor="#DDDDDD"
      borderRadius="16px"
      position="relative"
      zIndex={CREATIVE_PARTNER_SELECT_CONTROL_Z}
    >
      {partnershipAdvertiserValidationMessages.length > 0 &&
      !partnershipPartnerFieldsLoading &&
      !refreshingStatus ? (
        <Alert status="warning" size="sm" mb={3} borderRadius="md">
          <AlertIcon />
          <Box>
            {partnershipAdvertiserValidationMessages.map((message) => (
              <Text key={message} fontSize="sm">
                {message}
              </Text>
            ))}
          </Box>
        </Alert>
      ) : null}
      {partnershipOptionsError ? (
        <Alert status="info" size="sm" mb={3} borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            Partnership status could not be loaded ({partnershipOptionsError}).
            You can still pick a partner; publish may fail if Meta rejects the
            partnership.
          </Text>
        </Alert>
      ) : null}

      <HStack spacing={6} align="flex-start" flexWrap="wrap" w="full">
        <FormControl flex={1} minW="160px">
          <FormLabel {...accountFieldLabelProps}>
            Partner Facebook Page
          </FormLabel>
          <HStack align="flex-end" spacing={1} w="full">
            <Box flex={1} minW={0}>
              <Select
                options={partnershipPartnerFacebookOptions}
                isDisabled={
                  isDisabled || facebookLoading || partnerFacebookSelectDisabled
                }
                isLoading={facebookLoading || partnerFacebookPageFetchLoading}
                isClearable={
                  partnerFacebookPageSelected && !partnerFacebookSelectDisabled
                }
                value={selectedFacebookOption}
                onChange={(opt) => {
                  const selectedPage = opt?.value;
                  if (!selectedPage) {
                    setPartnerPageFetchedInstagramAccounts([]);
                    if (canClearPartnerFacebookKeepingInstagram) {
                      clearPartnerFacebookOnly();
                    } else {
                      clearPartnerFacebookSelection();
                    }
                    return;
                  }

                  userDismissedInferredPartnerFacebookRef.current = null;
                  userClearedPartnerInstagramRef.current = null;

                  const applyPartnerFacebookSelection = (
                    page: FacebookPage,
                    accounts: readonly InstagramAccount[],
                    fetchedAccounts?: readonly InstagramAccount[]
                  ) => {
                    const userClearedPartnerInstagramForPage =
                      userClearedPartnerInstagramRef.current ===
                      partnerFacebookPageIdentityKey(page.id);
                    const linkedFields = userClearedPartnerInstagramForPage
                      ? undefined
                      : partnerInstagramFieldsAfterFacebookPageSelected(
                          page,
                          accounts,
                          fetchedAccounts,
                          PARTNER_PAGE_LINKED_INSTAGRAM_INFERENCE
                        );
                    const resolvedPage = fetchedAccounts?.length
                      ? facebookPageWithResolvedLinkedInstagram(
                          page,
                          fetchedAccounts,
                          PARTNER_PAGE_LINKED_INSTAGRAM_INFERENCE
                        )
                      : page;
                    applyPartnerUpdate({
                      ...CLEAR_PARTNER_FACEBOOK,
                      partnerFacebookPageId: resolvedPage.id,
                      partnerFacebookPage: resolvedPage,
                      ...(linkedFields ?? {}),
                      partnerPermissionStatus: "UNKNOWN",
                    });
                    triggerPartnerPermissionLookupOnSelection();
                  };

                  let page = facebookPageWithCatalogLinkedMetadata(
                    selectedPage,
                    allFacebookPages
                  );
                  let accounts = allInstagramAccounts;
                  const linkedFields =
                    partnerInstagramFieldsFromLinkedFacebookPage(
                      page,
                      accounts
                    );
                  const linkedAccountInPartnerCatalog = linkedFields
                    ? resolvePartnerInstagramAccountByWire(
                        linkedFields.partnerInstagramUserId,
                        linkedFields.partnerInstagramUsername,
                        partnershipPartnerInstagramCatalog
                      )
                    : undefined;
                  applyPartnerFacebookSelection(page, accounts);

                  if (
                    !onPartnerFacebookPageChange ||
                    linkedAccountInPartnerCatalog
                  ) {
                    setPartnerPageFetchedInstagramAccounts([]);
                    return;
                  }

                  const pageId = page.id?.trim();
                  if (!pageId) {
                    setPartnerPageFetchedInstagramAccounts([]);
                    return;
                  }

                  const seq = ++partnerFacebookPageChangeSeq.current;
                  setPartnerPageFetchedInstagramAccounts([]);
                  setPartnerFacebookPageFetchLoading(true);
                  Promise.resolve(onPartnerFacebookPageChange(pageId))
                    .then((fetched) => {
                      if (partnerFacebookPageChangeSeq.current !== seq) {
                        return;
                      }
                      const rows = Array.isArray(fetched) ? fetched : [];
                      setPartnerPageFetchedInstagramAccounts(rows);
                      if (rows.length > 0) {
                        accounts = mergeInstagramAccounts([
                          ...allInstagramAccounts,
                          ...rows,
                        ]);
                      }
                      applyPartnerFacebookSelection(page, accounts, rows);
                    })
                    .finally(() => {
                      if (partnerFacebookPageChangeSeq.current === seq) {
                        setPartnerFacebookPageFetchLoading(false);
                      }
                    });
                }}
                placeholder={
                  partnershipPermissionLookupActive
                    ? "Loading partnership status…"
                    : facebookLoading
                      ? "Loading pages…"
                      : "Select partner page"
                }
                chakraStyles={partnerFacebookSelectStyles}
                formatOptionLabel={(option, meta) => (
                  <HStack spacing={1.5} align="center" w="full" minW={0}>
                    <HStack spacing={2} minW={0} flex={1} overflow="hidden">
                      <Avatar
                        size="xs"
                        name={option.value.name}
                        src={option.value.picture}
                        flexShrink={0}
                      />
                      <Text fontSize="13px" noOfLines={1} minW={0}>
                        {option.value.name}
                      </Text>
                    </HStack>
                    <PartnershipStatusBadge
                      variant={meta.context === "value" ? "dot" : "compact"}
                      partnershipSelectBadge={
                        meta.context === "value" &&
                        selectedPartnerSelectBadge &&
                        !partnerPermissionLookupLoading
                          ? selectedPartnerSelectBadge.partnership_select_badge
                          : option.value.partnership_select_badge
                      }
                      partnershipSelectBadgeLabel={
                        meta.context === "value" &&
                        selectedPartnerSelectBadge &&
                        !partnerPermissionLookupLoading
                          ? selectedPartnerSelectBadge.partnership_select_badge_label
                          : option.value.partnership_select_badge_label
                      }
                      unusableReason={option.value.partnership_unusable_reason}
                    />
                  </HStack>
                )}
              />
            </Box>
            {partnerFacebookPageSelected &&
            (partnerFacebookSelectDisabled ||
              partnerFacebookPageInferredFromInstagram) ? (
              <IconButton
                aria-label="Clear partner Facebook page"
                title={
                  canClearPartnerFacebookKeepingInstagram
                    ? "Clear partner Facebook page (keep Instagram)"
                    : "Clear partner Facebook page"
                }
                icon={<CloseIcon color="gray.500" fontSize={12} />}
                size="sm"
                variant="ghost"
                flexShrink={0}
                mb={1}
                onClick={() => {
                  if (canClearPartnerFacebookKeepingInstagram) {
                    clearPartnerFacebookOnly();
                    return;
                  }
                  clearPartnerFacebookSelection();
                }}
                isDisabled={isDisabled}
              />
            ) : null}
          </HStack>
        </FormControl>

        <FormControl flex={1} minW="160px">
          <FormLabel {...accountFieldLabelProps}>
            Partner Instagram Account{" "}
            {partnerInstagramOnlyMode ||
            partnerFacebookPageInferredFromInstagram ? (
              <Text as="span" fontWeight="normal" color="gray.500">
                (selected)
              </Text>
            ) : null}
          </FormLabel>
          <Select
            options={partnerInstagramSelectOptions}
            isDisabled={isDisabled || instagramLoading}
            isLoading={instagramLoading}
            isClearable={
              partnerInstagramSelected && !isDisabled && !instagramLoading
            }
            openMenuOnClick={!partnerInstagramSelectDisabled}
            openMenuOnFocus={!partnerInstagramSelectDisabled}
            value={selectedInstagramOption}
            onChange={(opt) => {
              if (!opt?.value) {
                clearPartnerInstagram();
                return;
              }
              if (partnerInstagramSelectDisabled) {
                return;
              }
              userDismissedInferredPartnerFacebookRef.current = null;
              userClearedPartnerInstagramRef.current = null;
              const existingPartnerPage = resolveCreativePartnerFacebookPage(
                creative,
                allFacebookPages
              );
              const partnerFacebookFields =
                partnerFacebookFieldsAfterInstagramSelected(
                  opt.value,
                  allFacebookPages,
                  allInstagramAccounts,
                  existingPartnerPage
                );
              applyPartnerUpdate({
                ...CLEAR_PARTNER_INSTAGRAM,
                partnerInstagramUserId: opt.value.id,
                partnerInstagramUsername:
                  opt.value.username?.trim() || undefined,
                partnerInstagramAccount: opt.value,
                ...(partnerFacebookFields ?? CLEAR_PARTNER_FACEBOOK),
                partnerPermissionStatus: "UNKNOWN",
              });
              triggerPartnerPermissionLookupOnSelection();
            }}
            placeholder={partnerInstagramPlaceholder}
            chakraStyles={partnerInstagramSelectStyles}
            formatOptionLabel={(option, meta) => (
              <HStack spacing={1.5} align="center" w="full" minW={0}>
                <HStack spacing={2} minW={0} flex={1} overflow="hidden">
                  <Avatar
                    size="xs"
                    name={option.value.username}
                    src={option.value.profile_picture_url}
                    flexShrink={0}
                  />
                  <Text fontSize="13px" noOfLines={1} minW={0}>
                    {option.value.username
                      ? `@${option.value.username}`
                      : option.value.name}
                  </Text>
                </HStack>
                <PartnershipStatusBadge
                  variant={meta.context === "value" ? "dot" : "compact"}
                  partnershipSelectBadge={
                    meta.context === "value" &&
                    selectedPartnerSelectBadge &&
                    !partnerPermissionLookupLoading
                      ? selectedPartnerSelectBadge.partnership_select_badge
                      : option.value.partnership_select_badge
                  }
                  partnershipSelectBadgeLabel={
                    meta.context === "value" &&
                    selectedPartnerSelectBadge &&
                    !partnerPermissionLookupLoading
                      ? selectedPartnerSelectBadge.partnership_select_badge_label
                      : option.value.partnership_select_badge_label
                  }
                  unusableReason={option.value.partnership_unusable_reason}
                />
              </HStack>
            )}
          />
        </FormControl>
      </HStack>

      {hasPartnershipCatalog &&
      selectedPartnerFacebookPage &&
      partnerMissingInstagramForRequest &&
      canRequestPartnership ? (
        <Text fontSize="xs" color="gray.600" mt={3}>
          To request partnership for a Facebook Page partner, select their
          Instagram account above when available, or connect Instagram to their
          Page in Meta Business Suite. Facebook-only requests use the
          advertiser&apos;s Facebook Page identity.
        </Text>
      ) : null}

      {partnershipStatusFollowUp}

      {hasPartnershipCatalog &&
      partnershipStatusAlertsReady &&
      selectedPartner &&
      partnerLaunchBlockMessage &&
      !partnershipRequestOutstanding &&
      !canRequestPartnership ? (
        <Text fontSize="sm" color="gray.600" mt={3}>
          {partnerLaunchBlockMessage}
        </Text>
      ) : null}

      <RequestPartnershipPermissionModal
        isOpen={requestModalOpen}
        partnerLabel={selectedPartner?.label ?? "this partner"}
        facebookPageOnly={Boolean(
          selectedPartner?.partnerFacebookPageId &&
            !selectedPartner.partnerInstagramUserId &&
            !selectedPartner.partnerInstagramUsername
        )}
        isLoading={requestLoading}
        errorMessage={requestError?.message}
        partnershipAdsHubUrl={requestError?.partnershipAdsHubUrl}
        onClose={() => setRequestModalOpen(false)}
        onConfirm={() => {
          void handleRequestPartnership();
        }}
      />
    </Box>
  );
}
